<script lang="ts">
export interface UtteranceToRender
  extends Pick<Utterance, "dialogueText" | "isContext">, Partial<Pick<Utterance, "startTime" | "endTime">> {
  speakerName: string;
  isSpeakerStart: boolean;
}
</script>
<script setup lang="ts" generic="T extends Utterance | Omit<Utterance, 'startTime' | 'endTime'>">
import { VList } from "virtua/vue";
import { computed, ref } from "vue";

import VoiceTranscriptUtterance, { type Slots } from "./VoiceTranscriptUtterance.vue";

import { cleanUtteranceText, isThisMe } from "@/services/UtilitiesService";

import { useMetadataStore } from "@/stores/metadata-store";

import type { ComponentPublicInstance } from "vue";
import type DialogueIdentity from "@/models/DialogueIdentity";
import type { Utterance } from "@/stores/case-file-store";

withDefaults(defineProps<Props>(), { virtualizeRendering: false, })

defineEmits<(e: "scroll") => void>();
defineSlots<Slots>();

const virtualScrollerRef = ref<InstanceType<typeof VList>>();
const renderedUtteranceRefs = ref<Record<number, HTMLElement | undefined>>({});

const dialogueIdentities = computed(
  () => useMetadataStore().files.find((f) => isThisMe(f, __props.fileReference))?.dialogueIdentities || "{}",
);

const utterancesToRender = computed(() => {
  let dialogueIdentity: Record<string, DialogueIdentity /* | undefined */> = {};
  try {
    dialogueIdentity = JSON.parse(dialogueIdentities.value);
  } catch (error) {}

  function getDialogueIdentity(identity: DialogueIdentity | undefined, index: string | number) {
    if (identity == null) return `Speaker: ${index}`;
    return `${identity.role}: ${identity.name || (identity.index ?? index)}`;
  }

  const result: UtteranceToRender[] = [];

  for (const cur of __props.list) {
    const dialogueText = cleanUtteranceText(cur.dialogueText);
    if (dialogueText === "") continue;

    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    const speakerName = getDialogueIdentity(dialogueIdentity[cur.speaker], cur.speaker);

    result.push({
      startTime: "startTime" in cur ? cur.startTime : undefined,
      endTime: "endTime" in cur ? cur.endTime : undefined,
      speakerName,
      dialogueText,
      isContext: cur.isContext,
      isSpeakerStart: result[result.length - 1]?.speakerName !== speakerName,
    });
  }

  return result;
});

defineExpose({ renderedUtteranceRefs, getItemOffset, scrollToIndex });

function scrollToIndex(index: number, align: "center" | "nearest" = "nearest") {
  if (__props.virtualizeRendering) {
    virtualScrollerRef.value?.scrollToIndex(index, { align, smooth: false });
    return;
  }

  renderedUtteranceRefs.value[index]?.scrollIntoView({ behavior: "instant", block: align });
}

function getItemOffset(index: number) {
  if (__props.virtualizeRendering) {
    if (virtualScrollerRef.value == null) return undefined as never;
    return virtualScrollerRef.value.getItemOffset(index) - virtualScrollerRef.value.scrollOffset;
  }

  // If the index provided does not have an explicit element, check if the supposed previous element exists
  // and return the bottom of that element, to signify the top of the new one
  const previousElement = renderedUtteranceRefs.value[index - 1];
  if (renderedUtteranceRefs.value[index] == null && previousElement != null) {
    return previousElement == null ? undefined : previousElement.offsetTop + previousElement.offsetHeight;
  }

  return renderedUtteranceRefs.value[index]?.offsetTop;
}

function setUtteranceRef(elRef: Element | ComponentPublicInstance | null, index: number) {
  if (elRef == null) return;
  renderedUtteranceRefs.value[index] = (elRef as unknown as InstanceType<typeof VoiceTranscriptUtterance>).element;
}

function removeUtteranceRef(index: number) {
  renderedUtteranceRefs.value[index] = undefined;
}

interface Props {
  fileReference: string;
  virtualizeRendering?: boolean;
  list: T[];
  getUtteranceProps?: T extends Utterance
    ? (utterance: Pick<Utterance, "startTime" | "endTime">, index: number) => Record<string, unknown>
    : undefined;
}
</script>
<template>
  <VList
    v-if="virtualizeRendering"
    ref="virtualScrollerRef"
    v-slot="{ item, index }: { item: UtteranceToRender; index: number }"
    class="tw-bg-white"
    :data="utterancesToRender"
    :overscan="15"
    @scroll="$emit('scroll')"
  >
    <VoiceTranscriptUtterance
      :ref="(el) => setUtteranceRef(el, index)"
      :index="index"
      :utterance="item"
      :data-index="index"
      :="
        item.startTime != null && item.endTime != null
          ? getUtteranceProps?.({ startTime: item.startTime, endTime: item.endTime }, index)
          : undefined
      "
      @vue:unmounted="() => removeUtteranceRef(index)"
    >
      <template v-if="$slots.beforeUtterance" #beforeUtterance>
        <slot name="beforeUtterance" :index="index" />
      </template>
    </VoiceTranscriptUtterance>
  </VList>
  <div v-else @scroll="$emit('scroll')">
    <VoiceTranscriptUtterance
      v-for="(item, index) in utterancesToRender"
      :ref="(el) => setUtteranceRef(el, index)"
      :key="`${item.speakerName}-${index}`"
      :index="index"
      :utterance="item"
      :="
        item.startTime != null && item.endTime != null
          ? getUtteranceProps?.({ startTime: item.startTime, endTime: item.endTime }, index)
          : undefined
      "
      @vue:unmounted="() => removeUtteranceRef(index)"
    >
      <template v-if="$slots.beforeUtterance" #beforeUtterance>
        <slot name="beforeUtterance" :index="index" />
      </template>
    </VoiceTranscriptUtterance>
  </div>
</template>
